import { ProductCostData, PortfolioProduct, SimulationInputs, AggregatedCostData, SimulationResults } from './types';

/**
 * Extract products with weights/countervalues from portfolio data
 */
export function extractProductsFromPortfolioData(portfolioData: any): PortfolioProduct[] {
  // Get assets array from various possible paths
  const assets = portfolioData?.data?.data?.assets || portfolioData?.data?.assets || portfolioData?.assets;

  if (!Array.isArray(assets)) {
    return [];
  }

  return assets
    .filter((asset: any) => asset?.isin)
    .map((asset: any) => ({
      isin: asset.isin,
      weight: asset.weight != null ? parseFloat(asset.weight) : 0,
      countervalue: asset.value != null ? parseFloat(asset.value) : 0,
    }));
}

/**
 * A policy without cost data leaves the instrument-cost base (Fineco, 20 Oct 2026);
 * once its costs are filled it counts like any other product.
 */
function isPolicyWithoutCosts(cost: ProductCostData): boolean {
  return cost.asset_type === 'POLIZZE' && cost.Ongoing_Cost_Est == null && cost.Transaction_Fee_Est == null;
}

/**
 * Calculate weighted average of a cost field
 */
function calculateWeightedAverage(
  products: PortfolioProduct[],
  costData: ProductCostData[],
  field: keyof ProductCostData,
  excludeFAM: boolean = false,
  excludePoliciesWithoutCosts: boolean = false,
): number {
  const costMap = new Map(costData.map((c) => [c.isin, c]));
  let totalWeight = 0;
  let weightedSum = 0;

  for (const product of products) {
    const cost = costMap.get(product.isin)!;
    if (excludeFAM && cost.is_fam_fund) continue;
    if (excludePoliciesWithoutCosts && isPolicyWithoutCosts(cost)) continue;

    const weight = product.weight || 0;
    const value = typeof cost[field] === 'number' ? cost[field] : 0;
    weightedSum += value * weight;
    totalWeight += weight;
  }

  return totalWeight > 0 ? weightedSum / totalWeight : 0;
}

/**
 * Calculate weighted rebate contribution for FONDI assets only (with maggiorazione)
 * Returns a value such that portfolioValue × result / 100 = total rebates in Euro
 *
 * This avoids double-dilution: we sum rebates weighted by portfolio weight
 * for FONDI assets only, since only FONDI generate rebates.
 */
function calculateFondiRebatesWeightedSum(products: PortfolioProduct[], costData: ProductCostData[]): number {
  const costMap = new Map(costData.map((c) => [c.isin, c]));
  let weightedSum = 0;

  for (const product of products) {
    const cost = costMap.get(product.isin)!;
    if (cost.asset_type !== 'FONDI') continue;

    const weight = product.weight || 0;
    const rebates = typeof cost.fineco_mng_rebates === 'number' ? cost.fineco_mng_rebates : 0;
    const multiplier = cost.rebates_multiplier ?? 1.0;
    weightedSum += rebates * multiplier * weight;
  }

  return weightedSum;
}

/**
 * Aggregate cost data at portfolio level
 */
export function aggregateCostData(products: PortfolioProduct[], costData: ProductCostData[]): AggregatedCostData {
  const managingFee = calculateWeightedAverage(products, costData, 'managing_fee', false, true);
  const ongoingCost = calculateWeightedAverage(products, costData, 'Ongoing_Cost_Est', false, true);

  return {
    commissioniGestione: managingFee,
    altreCommissioniRicorrenti: ongoingCost - managingFee,
    commissioniTransazione: calculateWeightedAverage(products, costData, 'Transaction_Fee_Est', false, true),
    commissioniPerformance: calculateWeightedAverage(products, costData, 'Performance_Fee_Est', false, true),
    abbuoniPercentage: calculateWeightedAverage(products, costData, 'fineco_mng_rebates'),
  };
}

/**
 * Value the instrument costs are taken on: the portfolio less its policies without cost data
 * Note: weight is in decimal form (e.g., 0.30 means 30%, 1.0 means 100%)
 */
function calculateInstrumentCostBase(
  products: PortfolioProduct[],
  costData: ProductCostData[],
  totalValue: number,
): number {
  const costMap = new Map(costData.map((c) => [c.isin, c]));
  let policyWeightDecimal = 0;

  for (const product of products) {
    if (isPolicyWithoutCosts(costMap.get(product.isin)!)) {
      policyWeightDecimal += product.weight || 0;
    }
  }

  return totalValue * (1 - policyWeightDecimal);
}

/**
 * Calculate portfolio value excluding FAM funds
 * Note: weight is in decimal form (e.g., 0.30 means 30%, 1.0 means 100%)
 */
function calculateValueExcludingFAM(
  products: PortfolioProduct[],
  costData: ProductCostData[],
  totalValue: number,
): number {
  const costMap = new Map(costData.map((c) => [c.isin, c]));
  let famWeightDecimal = 0;

  for (const product of products) {
    const cost = costMap.get(product.isin)!;
    if (cost.is_fam_fund) {
      famWeightDecimal += product.weight || 0;
    }
  }

  return totalValue * (1 - famWeightDecimal);
}

/**
 * Calculate portfolio value for a specific asset type
 * Note: weight is in decimal form (e.g., 0.30 means 30%, 1.0 means 100%)
 */
function calculateValueByAssetType(
  products: PortfolioProduct[],
  costData: ProductCostData[],
  assetType: 'FONDI' | 'ETF' | 'BOND',
  totalValue: number,
  excludeFAM: boolean = false,
): number {
  const costMap = new Map(costData.map((c) => [c.isin, c]));
  let typeWeightDecimal = 0;

  for (const product of products) {
    const cost = costMap.get(product.isin)!;
    if (cost.asset_type !== assetType) continue;
    if (excludeFAM && cost.is_fam_fund) continue;

    typeWeightDecimal += product.weight || 0;
  }

  return totalValue * typeWeightDecimal;
}

/**
 * ============================================================================
 * FEE ONLY MODE - Calculation Logic
 * ============================================================================
 *
 * Advisory Fees:
 *   - Commissione consulenza: 0-2.20% (user input)
 *   - Servizi aggiuntivi: 0.00% or 0.20% (user input)
 *
 * Abbuoni (Rebates):
 *   - Calculated on FONDI portion only (not ETF or BOND)
 *   - Uses maggiorazione multiplier (e.g., 1.2 for products with maggiorazione)
 *   - Capped at (Commissione consulenza + Servizi aggiuntivi)
 *
 * Formulas:
 *   - Addebito netto = max(0, Fee + Servizi - Abbuoni)
 *   - Costo totale = max(0, Fee + Servizi + Strumenti - Abbuoni)
 *   - Stima rebates PFA = 45% × (Fee + Servizi)
 */
function calculateFeeOnlyResults(
  inputs: SimulationInputs,
  aggregatedData: AggregatedCostData,
  products: PortfolioProduct[],
  costData: ProductCostData[],
): SimulationResults {
  const portfolioValue = inputs.portfolioValue;
  const { commissioneConsulenza, serviziAggiuntivi } = inputs.feeOnly;

  // --- Advisory Fees (applied to full portfolio value) ---
  const commissioneConsulenzaEuro = (portfolioValue * commissioneConsulenza) / 100;
  const serviziAggiuntiviEuro = (portfolioValue * serviziAggiuntivi) / 100;

  // --- Abbuoni (Rebates) ---
  // Calculated directly from FONDI assets with maggiorazione multiplier
  const abbuoniWeightedSum = calculateFondiRebatesWeightedSum(products, costData);
  const abbuoniEuroRaw = (portfolioValue * abbuoniWeightedSum) / 100;
  // Capped at (Fee + Servizi) - client can't receive more rebates than they pay in advisory fees
  const abbuoniEuro = Math.min(abbuoniEuroRaw, commissioneConsulenzaEuro + serviziAggiuntiviEuro);

  // --- Instrument Costs (Strumenti) ---
  const instrumentCostBase = calculateInstrumentCostBase(products, costData, portfolioValue);
  const commissioniGestioneEuro = (instrumentCostBase * aggregatedData.commissioniGestione) / 100;
  const altreCommissioniRicorrentiEuro = (instrumentCostBase * aggregatedData.altreCommissioniRicorrenti) / 100;
  const commissioniTransazioneEuro = (instrumentCostBase * aggregatedData.commissioniTransazione) / 100;
  const commissioniPerformanceEuro = (instrumentCostBase * aggregatedData.commissioniPerformance) / 100;
  const strumentiEuro =
    commissioniGestioneEuro + altreCommissioniRicorrentiEuro + commissioniTransazioneEuro + commissioniPerformanceEuro;

  // --- Summary Calculations ---
  // Addebito netto = max(0, Fee + Servizi - Abbuoni)
  const addebitiNettiCCEuro = Math.max(0, commissioneConsulenzaEuro + serviziAggiuntiviEuro - abbuoniEuro);

  // Costo totale = max(0, Fee + Servizi + Strumenti - Abbuoni)
  const totaleCostiEuro = Math.max(0, commissioneConsulenzaEuro + serviziAggiuntiviEuro + strumentiEuro - abbuoniEuro);

  // Stima rebates PFA = 45% × (Fee + Servizi) - NO maggiorazione here
  const stimaRebatesPfaEuro = 0.45 * (commissioneConsulenzaEuro + serviziAggiuntiviEuro);

  // --- Convert to Percentages ---
  const totaleCostiPercentage = portfolioValue > 0 ? (totaleCostiEuro / portfolioValue) * 100 : 0;
  const addebitiNettiCCPercentage = portfolioValue > 0 ? (addebitiNettiCCEuro / portfolioValue) * 100 : 0;
  const stimaRebatesPfaPercentage = portfolioValue > 0 ? (stimaRebatesPfaEuro / portfolioValue) * 100 : 0;

  return {
    // Advisory fees
    commissioneConsulenzaEuro,
    serviziAggiuntiviEuro,
    // Differenziata fees (not used in Fee Only)
    feeFondiEuro: 0,
    feeETFEuro: 0,
    feeAmministratoEuro: 0,
    // Rebates
    abbuoniEuro,
    // Instrument costs
    commissioniGestioneEuro,
    altreCommissioniRicorrentiEuro,
    commissioniTransazioneEuro,
    commissioniPerformanceEuro,
    // Summary
    totaleCostiEuro,
    totaleCostiPercentage,
    addebitiNettiCCEuro,
    addebitiNettiCCPercentage,
    stimaRebatesPfaEuro,
    stimaRebatesPfaPercentage,
  };
}

/**
 * ============================================================================
 * FEE ON TOP MODE - Calculation Logic
 * ============================================================================
 *
 * Two sub-modes:
 *   - Unica: Uses Commissione consulenza (0-1.00%)
 *   - Differenziata: Uses Fee Fondi + Fee ETF + Fee Amministrato
 *
 * Advisory Fees:
 *   - If Unica: Commissione consulenza applied to base value
 *   - If Differenziata: Fee Fondi (on FONDI), Fee ETF (on ETF), Fee Amministrato (on full)
 *   - Servizi aggiuntivi: 0.00% or 0.20%
 *
 * FAM Exclusion Option:
 *   - When enabled, FAM funds are excluded from fee calculations
 *
 * NO Abbuoni in Fee on Top mode
 *
 * Formulas:
 *   - Addebito netto = Advisory fees + Servizi
 *   - Costo totale = Advisory fees + Servizi + Strumenti
 *   - Stima rebates PFA = 45% × (Advisory fees + Servizi + fineco_mng_rebates)
 *     Note: Uses STANDARD rebates percentage (no maggiorazione)
 */
function calculateFeeOnTopResults(
  inputs: SimulationInputs,
  aggregatedData: AggregatedCostData,
  products: PortfolioProduct[],
  costData: ProductCostData[],
): SimulationResults {
  const portfolioValue = inputs.portfolioValue;
  const { commissioneConsulenza, serviziAggiuntivi, feeFondi, feeETF, feeAmministrato, includiFondiFAM } =
    inputs.feeOnTop;

  // --- Base Value for Fees (considering FAM exclusion) ---
  const baseValueForFees = !includiFondiFAM
    ? calculateValueExcludingFAM(products, costData, portfolioValue)
    : portfolioValue;

  // --- Advisory Fees: Unica Mode ---
  const commissioneConsulenzaEuro = (baseValueForFees * commissioneConsulenza) / 100;
  const serviziAggiuntiviEuro = (baseValueForFees * serviziAggiuntivi) / 100;

  // --- Advisory Fees: Differenziata Mode ---
  // Fee Fondi/ETF apply to their respective asset type portions
  const fondiValue = calculateValueByAssetType(products, costData, 'FONDI', portfolioValue, !includiFondiFAM);
  const etfValue = calculateValueByAssetType(products, costData, 'ETF', portfolioValue, !includiFondiFAM);

  const feeFondiEuro = feeFondi ? (fondiValue * feeFondi) / 100 : 0;
  const feeETFEuro = feeETF ? (etfValue * feeETF) / 100 : 0;
  // Fee Amministrato applies only to the Amministrato bucket (excluding Fondi and ETF portions)
  const amministratoValue = Math.max(0, baseValueForFees - fondiValue - etfValue);
  const feeAmministratoEuro = feeAmministrato ? (amministratoValue * feeAmministrato) / 100 : 0;

  // --- Determine which advisory fees to use ---
  // If Commissione consulenza > 0, use Unica mode; otherwise use Differenziata
  const advisoryFeesEuro =
    commissioneConsulenzaEuro > 0 ? commissioneConsulenzaEuro : feeFondiEuro + feeETFEuro + feeAmministratoEuro;

  // --- Instrument Costs (Strumenti) ---
  const instrumentCostBase = calculateInstrumentCostBase(products, costData, portfolioValue);
  const commissioniGestioneEuro = (instrumentCostBase * aggregatedData.commissioniGestione) / 100;
  const altreCommissioniRicorrentiEuro = (instrumentCostBase * aggregatedData.altreCommissioniRicorrenti) / 100;
  const commissioniTransazioneEuro = (instrumentCostBase * aggregatedData.commissioniTransazione) / 100;
  const commissioniPerformanceEuro = (instrumentCostBase * aggregatedData.commissioniPerformance) / 100;
  const strumentiEuro =
    commissioniGestioneEuro + altreCommissioniRicorrentiEuro + commissioniTransazioneEuro + commissioniPerformanceEuro;

  // --- Summary Calculations ---
  // Addebito netto = Advisory fees + Servizi (no rebates in Fee on Top)
  const addebitiNettiCCEuro = advisoryFeesEuro + serviziAggiuntiviEuro;

  // Costo totale = Advisory fees + Servizi + Strumenti
  const totaleCostiEuro = addebitiNettiCCEuro + strumentiEuro;

  // Stima rebates PFA = 45% × (Advisory fees + Servizi + fineco_mng_rebates)
  // Uses STANDARD rebates percentage (no maggiorazione)
  const rebatesBaseEuro = (portfolioValue * aggregatedData.abbuoniPercentage) / 100;
  const stimaRebatesPfaEuro = 0.45 * (advisoryFeesEuro + serviziAggiuntiviEuro + rebatesBaseEuro);

  // --- Convert to Percentages ---
  const totaleCostiPercentage = portfolioValue > 0 ? (totaleCostiEuro / portfolioValue) * 100 : 0;
  const addebitiNettiCCPercentage = portfolioValue > 0 ? (addebitiNettiCCEuro / portfolioValue) * 100 : 0;
  const stimaRebatesPfaPercentage = portfolioValue > 0 ? (stimaRebatesPfaEuro / portfolioValue) * 100 : 0;

  return {
    // Advisory fees (Unica)
    commissioneConsulenzaEuro,
    serviziAggiuntiviEuro,
    // Advisory fees (Differenziata)
    feeFondiEuro,
    feeETFEuro,
    feeAmministratoEuro,
    // No rebates in Fee on Top
    abbuoniEuro: 0,
    // Instrument costs
    commissioniGestioneEuro,
    altreCommissioniRicorrentiEuro,
    commissioniTransazioneEuro,
    commissioniPerformanceEuro,
    // Summary
    totaleCostiEuro,
    totaleCostiPercentage,
    addebitiNettiCCEuro,
    addebitiNettiCCPercentage,
    stimaRebatesPfaEuro,
    stimaRebatesPfaPercentage,
  };
}

/**
 * Find products that have null values in any cost attribute (except rebates_multiplier)
 * Only checks FONDI and ETF products, excludes BONDs
 * Returns an array of ISINs for products with missing cost data
 */
export function findProductsWithMissingCostData(costData: ProductCostData[]): string[] {
  const costFields: (keyof ProductCostData)[] = [
    'managing_fee',
    'Ongoing_Cost_Est',
  ];

  return costData
    .filter(
      (product) =>
        (product.asset_type === 'FONDI' || product.asset_type === 'ETF') &&
        costFields.some((field) => product[field] === null),
    )
    .map((product) => product.isin);
}

/**
 * Insurance policies without cost data (n.d.): left out of the instrument costs;
 * the dialog says so in a note.
 */
export function findPoliciesWithoutCostData(costData: ProductCostData[]): string[] {
  return costData.filter(isPolicyWithoutCosts).map((product) => product.isin);
}

/**
 * Calculate simulation results based on inputs
 * Delegates to mode-specific functions for clarity
 */
export function calculateSimulationResults(
  inputs: SimulationInputs,
  aggregatedData: AggregatedCostData,
  products: PortfolioProduct[],
  costData: ProductCostData[],
): SimulationResults {
  if (inputs.feeRegime === 'fee_only') {
    return calculateFeeOnlyResults(inputs, aggregatedData, products, costData);
  } else {
    return calculateFeeOnTopResults(inputs, aggregatedData, products, costData);
  }
}
